// React
import React from "react";
// redux hooks
import { useFieldArray, Controller } from "react-hook-form";
// BASE components
import BASESvgIcon from '../../../../../../@BASE/core/BASESvgIcon';
// material-ui components
import Accordion from '@mui/material/Accordion';
import AccordionActions from '@mui/material/AccordionActions';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from "@mui/system/Box";
import Button from "@mui/material/Button"
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from "@mui/material/IconButton";
import Checkbox from '@mui/material/Checkbox';
import TextField from "@mui/material/TextField";
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import Typography from "@mui/material/Typography";
// custom components
import NumberedHeading from "../../../../../shared-components/forms/NumberedHeading";
import SummaryStatementArray from "./SummaryStatementArray";
import WYSIWYGEditor from "../../../../../shared-components/WYSIWYGEditor";

export default function SummaryStatementSetArray({ control, register, setValue, getValues, errors, inputDir }) {
  // setup field array
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'content.statement_sets',
  });

  return (
    <>
      <Timeline
        sx={{
          [`& .${timelineItemClasses.root}:before`]: {
            flex: 0,
            padding: 0,
          },
        }}
        className="px-0"
      >
        {fields.map((item, index) => {
          return (
            <TimelineItem key={item.id}>
              <TimelineSeparator>
                <TimelineDot color="secondary" sx={{marginTop: '22px'}} />
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent className="py-0">
                <Accordion
                  defaultExpanded
                  className="bg-transparent shadow-none"
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`${index}`}
                    id={`${index}`}
                    className="py-0 px-16 m-0 rounded bg-white shadow-sm"
                    sx={{
                      border: 'solid 1px',
                      borderColor: '#bdbdbd'
                    }}
                  >
                    <Typography className="grow m-0 font-semibold">
                      Statement Set {index + 1}
                    </Typography>
                    {index !== 0 &&
                      <IconButton 
                        className="ml-16 mr-8"
                        aria-label="move up"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          move(index, index - 1);
                        }}
                      >
                        <BASESvgIcon size={20}>heroicons-solid:arrow-circle-up</BASESvgIcon>
                      </IconButton>
                    }
                    {index !== fields.length - 1 &&
                      <IconButton 
                        className="mx-8"
                        aria-label="move down"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          move(index, index + 1);
                        }}
                      >
                        <BASESvgIcon size={20}>heroicons-solid:arrow-circle-down</BASESvgIcon>
                      </IconButton>
                    }
                    <IconButton
                      className="ml-8 mr-16"
                      aria-label="delete"
                      color="error"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(index);
                      }}
                    >
                      <BASESvgIcon size={20}>heroicons-outline:trash</BASESvgIcon>
                    </IconButton>
                  </AccordionSummary>
                  <AccordionDetails className="p-0">
                    <Box
                      className="p-0"
                    >
                      <NumberedHeading number="1" title="Original" instructions="Enter the original sentence each option is created from." />
                      <Controller
                        control={control}
                        name={`content.statement_sets.${index}.original`}
                        render={({ field }) => (
                          <>
                            <TextField
                              {...field}
                              className="mt-16 mb-16"
                              label="Text"
                              placeholder="Text"
                              error={!!errors.content?.statement_sets?.[index]?.original}
                              helperText={errors.content?.statement_sets?.[index]?.original?.message}
                              fullWidth
                              multiline
                              minRows={1}
                              maxRows={5}
                              required
                              dir={inputDir}
                            />
                          </>
                        )}
                      /> 

                      <Divider className="mt-16" />

                      <NumberedHeading number="2" title="Statements" instructions='Using the controls below, please add statements for the Statement.' />
                      <Controller
                        control={control}
                        name={`content.statement_sets.${index}.statements`}
                        render={({ field }) => <SummaryStatementArray nestIndex={index} inputDir={inputDir} {...{ control, errors }} />}
                      />

                      <Divider className="mt-16" />

                      <NumberedHeading number="3" title="Correct Feedback" instructions="Enter the feedback for a correct response" />
                      <Controller
                        control={control}
                        name={`content.statement_sets.${index}.correct_feedback`}
                        render={({ field }) => (
                          <>
                            <TextField
                              {...field}
                              className="mt-16 mb-16"
                              label="Text"
                              placeholder="Text"
                              error={!!errors.content?.statement_sets?.[index]?.correct_feedback}
                              helperText={errors.content?.statement_sets?.[index]?.correct_feedback?.message}
                              fullWidth
                              multiline
                              minRows={1}
                              maxRows={5}
                              required
                              dir={inputDir}
                            />
                          </>
                        )}
                      /> 

                      <Divider className="mt-16" />

                      <NumberedHeading number="4" title="First Incorrect Feedback" instructions="Enter the feedback for the first incorrect response." />
                      <Controller
                        control={control}
                        name={`content.statement_sets.${index}.incorrect_feedback_1`}
                        render={({ field }) => (
                          <>
                            <TextField
                              {...field}
                              className="mt-16 mb-16"
                              label="Text"
                              placeholder="Text"
                              error={!!errors.content?.statement_sets?.[index]?.incorrect_feedback_1}
                              helperText={errors.content?.statement_sets?.[index]?.incorrect_feedback_1?.message}
                              fullWidth
                              multiline
                              minRows={1}
                              maxRows={5}
                              required
                              dir={inputDir}
                            />
                          </>
                        )}
                      /> 

                      <Divider className="mt-16" />

                      <NumberedHeading number="5" title="Second Incorrect Feedback" instructions="Enter the feedback for the second incorrect response." />
                      <Controller
                        control={control}
                        name={`content.statement_sets.${index}.incorrect_feedback_2`}
                        render={({ field }) => (
                          <>
                            <TextField
                              {...field}
                              className="mt-16 mb-16"
                              label="Text"
                              placeholder="Text"
                              error={!!errors.content?.statement_sets?.[index]?.incorrect_feedback_2}
                              helperText={errors.content?.statement_sets?.[index]?.incorrect_feedback_2?.message}
                              fullWidth
                              multiline
                              minRows={1}
                              maxRows={5}
                              required
                              dir={inputDir}
                            />
                          </>
                        )}
                      /> 

                      <Divider className="mt-16" />

                      <NumberedHeading number="6" title="Final Incorrect Feedback" instructions="Enter the feedback for the final incorrect response." />
                      <Controller
                        control={control}
                        name={`content.statement_sets.${index}.incorrect_feedback_3`}
                        render={({ field }) => (
                          <>
                            <TextField
                              {...field}
                              className="mt-16 mb-16"
                              label="Text"
                              placeholder="Text"
                              error={!!errors.content?.statement_sets?.[index]?.incorrect_feedback_3}
                              helperText={errors.content?.statement_sets?.[index]?.incorrect_feedback_3?.message}
                              fullWidth
                              multiline
                              minRows={1}
                              maxRows={5}
                              required
                              dir={inputDir}
                            />
                          </>
                        )}
                      /> 

                      <Divider className="mt-16" />

                      <NumberedHeading number="7" title="Max Number of Tries" instructions="Enter the amount of tries the learner should get." />
                      <Controller
                        control={control}
                        name={`content.statement_sets.${index}.max_number_of_tries`}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            className="mt-16 mb-16"
                            label="Max Number of Tries"
                            placeholder="Max Number of Tries"
                            error={!!errors.content?.statement_sets?.[index]?.max_number_of_tries}
                            helperText={errors.content?.statement_sets?.[index]?.max_number_of_tries?.message}
                            fullWidth
                            required
                            type="number"
                          />
                        )}
                      />
                      <Divider className="mt-16" />
                      
                    </Box>
                  </AccordionDetails>
                  <AccordionActions>
                    {index !== 0 &&
                      <Button
                        variant="outlined"
                        onClick={() => move(index, index - 1)}
                        startIcon={<BASESvgIcon size={20}>heroicons-solid:arrow-circle-up</BASESvgIcon>}
                        size="small"
                      >
                        Move Up
                      </Button>
                    }
                    {index !== fields.length - 1 &&
                      <Button
                        variant="outlined"
                        onClick={() => move(index, index + 1)}
                        startIcon={<BASESvgIcon size={20}>heroicons-solid:arrow-circle-down</BASESvgIcon>}
                        size="small"
                      >
                        Move Down
                      </Button>
                    }
                    <Button
                      variant="contained"
                      onClick={() => remove(index)}
                      color="error"
                      startIcon={<BASESvgIcon size={20}>heroicons-outline:trash</BASESvgIcon>}
                      size="small"
                    >
                      Remove
                    </Button>
                  </AccordionActions>
                </Accordion>
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
      <Box className="flex items-center mt-0 p-0">
        <Button
          className="group flex items-center mt-8 rounded cursor-pointer"
          color="secondary"
          variant="contained"
          onClick={() => append({ 
            original: "",
            statements: [
              {
                lure_text: "",
                is_correct: false,
                type: ""
              },
            ],
            correct_feedback: "",
            incorrect_feedback_1: "",
            incorrect_feedback_2: "",
            incorrect_feedback_3: "",
            max_number_of_tries: 3
          })}
        >
          <BASESvgIcon size={20}>heroicons-solid:plus-circle</BASESvgIcon>
          <span className="ml-8 font-medium text-secondary group-hover:underline">
            Add Statement
          </span>
        </Button>
      </Box>
    </>
  );
}
